import {
  useCallback, useRef, useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSSEStream } from '@/common/hooks/useSSEStream';
import { getGetModulesQueryKey, getGetModuleVersionQueryKey } from '@/common/api/endpoints/modules/modules';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import {
  useAssistantPageContextStore,
  type AssistantPageContext,
} from './assistantPageContextStore';

export type AssistantChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type AssistantStreamItem =
  | {
    id: string;
    kind: 'user';
    content: string;
  }
  | {
    id: string;
    kind: 'assistant';
    content: string;
  }
  | {
    id: string;
    kind: 'activity';
    turnId: string;
    activityType: 'tool_call' | 'tool_result';
    operation: string;
    summary?: string;
    error?: string;
  }
  | {
    id: string;
    kind: 'refresh';
    scopes: string[];
  };

export type PendingConfirmation = {
  operation: string;
  summary: string;
  confirmToken: string;
  input: Record<string, unknown>;
  previewData?: Record<string, unknown>;
};

export type AssistantStreamEvent = {
  type: string;
  content?: string;
  name?: string;
  operation?: string;
  summary?: string;
  confirmToken?: string;
  input?: Record<string, unknown>;
  data?: Record<string, unknown>;
  arguments?: Record<string, unknown>;
  error?: string;
  scopes?: string[];
};

const ASSISTANT_CHAT_PATH = '/admin/assistant/chat';

const buildRequestPayload = (
  payload: {
    messages: AssistantChatMessage[];
    confirmToken?: string;
    confirmOperation?: string;
    confirmInput?: Record<string, unknown>;
  },
  pageContext: AssistantPageContext | null,
) => ({
  messages: payload.messages.map(({ role, content }) => ({ role, content })),
  pageContext: pageContext || undefined,
  confirmToken: payload.confirmToken,
  confirmOperation: payload.confirmOperation,
  confirmInput: payload.confirmInput,
});

const isChatStreamItem = (
  item: AssistantStreamItem,
): item is Extract<AssistantStreamItem, { kind: 'user' | 'assistant' }> => (
  item.kind === 'user' || item.kind === 'assistant'
);

const streamItemToChatMessage = (
  item: Extract<AssistantStreamItem, { kind: 'user' | 'assistant' }>,
): AssistantChatMessage => ({
  role: item.kind,
  content: item.content,
});

const invalidateContextScopes = (
  queryClient: ReturnType<typeof useQueryClient>,
  scopes: string[],
  pageContext: AssistantPageContext | null,
) => {
  scopes.forEach((scope) => {
    if (scope === 'modules') {
      queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() });
    }
    if (scope === 'currentModule' && pageContext?.moduleId && pageContext?.version) {
      queryClient.invalidateQueries({
        queryKey: getGetModuleVersionQueryKey(pageContext.moduleId, pageContext.version),
      });
    }
    if ((scope === 'tasks' || scope === 'resources') && pageContext?.moduleId && pageContext?.version) {
      queryClient.invalidateQueries({
        queryKey: getGetTasksByModuleVersionQueryKey(pageContext.moduleId, pageContext.version),
      });
    }
  });
};

/**
 * useAssistantStream
 * ----
 * Thin SSE hook for the admin assistant chat endpoint.
 */
export const useAssistantStream = () => {
  const queryClient = useQueryClient();
  const pageContext = useAssistantPageContextStore((state) => state.pageContext);
  const [items, setItems] = useState<AssistantStreamItem[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamingAssistantIdRef = useRef<string | null>(null);
  const turnIdRef = useRef<string | null>(null);
  const previewDataRef = useRef<Record<string, unknown> | null>(null);

  const appendAssistantDelta = useCallback((delta: string) => {
    const assistantId = streamingAssistantIdRef.current;
    if (!assistantId || !delta) return;
    setItems((prev) => prev.map((item) => (
      item.id === assistantId && item.kind === 'assistant'
        ? { ...item, content: `${item.content}${delta}` }
        : item
    )));
  }, []);

  const appendActivity = useCallback((activity: Omit<Extract<AssistantStreamItem, { kind: 'activity' }>, 'id'>) => {
    setItems((prev) => [
      ...prev,
      {
        id: `activity-${Date.now()}-${prev.length}`,
        ...activity,
      },
    ]);
  }, []);

  const handleMessage = useCallback((event: AssistantStreamEvent) => {
    if (event.type === 'text_delta' && event.content) {
      if (!streamingAssistantIdRef.current) {
        const assistantId = `assistant-${Date.now()}`;
        streamingAssistantIdRef.current = assistantId;
        setItems((prev) => [...prev, { id: assistantId, kind: 'assistant', content: '' }]);
      }
      appendAssistantDelta(event.content);
      return;
    }
    if (event.type === 'tool_call') {
      appendActivity({
        kind: 'activity',
        turnId: turnIdRef.current || 'unknown',
        activityType: 'tool_call',
        operation: event.name || event.operation || 'unknown',
      });
      return;
    }
    if (event.type === 'tool_result') {
      appendActivity({
        kind: 'activity',
        turnId: turnIdRef.current || 'unknown',
        activityType: 'tool_result',
        operation: event.operation || 'unknown',
        summary: event.summary,
        error: event.error,
      });
      if (event.error) {
        appendAssistantDelta(event.error);
      }
      return;
    }
    if (event.type === 'context_refresh' && Array.isArray(event.scopes)) {
      invalidateContextScopes(queryClient, event.scopes, pageContext);
      setItems((prev) => [
        ...prev,
        {
          id: `refresh-${Date.now()}-${prev.length}`,
          kind: 'refresh',
          scopes: event.scopes as string[],
        },
      ]);
      return;
    }
    if (event.type === 'tool_preview') {
      previewDataRef.current = event.data || null;
      return;
    }
    if (event.type === 'awaiting_confirmation' && event.confirmToken && event.operation) {
      setPendingConfirmation({
        operation: event.operation,
        summary: event.summary || '',
        confirmToken: event.confirmToken,
        input: event.input || {},
        previewData: previewDataRef.current || undefined,
      });
      previewDataRef.current = null;
    }
  }, [appendActivity, appendAssistantDelta, pageContext, queryClient]);

  const handleServerClose = useCallback(() => {
    setIsStreaming(false);
    streamingAssistantIdRef.current = null;
    turnIdRef.current = null;
  }, []);

  const { startStream, stopStream } = useSSEStream<AssistantStreamEvent, Record<string, unknown>>({
    source: ASSISTANT_CHAT_PATH,
    method: 'POST',
    onMessage: handleMessage,
    onServerClose: handleServerClose,
    onError: () => {
      setIsStreaming(false);
      streamingAssistantIdRef.current = null;
      turnIdRef.current = null;
    },
  });

  const sendTurn = useCallback(async (payload: {
    messages: AssistantChatMessage[];
    confirmToken?: string;
    confirmOperation?: string;
    confirmInput?: Record<string, unknown>;
  }) => {
    setIsStreaming(true);
    setPendingConfirmation(null);
    const turnId = `turn-${Date.now()}`;
    turnIdRef.current = turnId;

    await startStream(
      ASSISTANT_CHAT_PATH,
      buildRequestPayload(payload, pageContext),
    );
  }, [pageContext, startStream]);

  const sendUserMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userMessage: AssistantChatMessage = {
      role: 'user',
      content: trimmed,
    };
    const nextMessages = [
      ...items.filter(isChatStreamItem).map(streamItemToChatMessage),
      userMessage,
    ];
    setItems((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        kind: 'user',
        content: trimmed,
      },
    ]);
    await sendTurn({ messages: nextMessages });
  }, [items, sendTurn]);

  const confirmPending = useCallback(async () => {
    if (!pendingConfirmation) return;
    const chatMessages = items.filter(isChatStreamItem).map(streamItemToChatMessage);
    await sendTurn({
      messages: chatMessages,
      confirmToken: pendingConfirmation.confirmToken,
      confirmOperation: pendingConfirmation.operation,
      confirmInput: pendingConfirmation.input,
    });
  }, [items, pendingConfirmation, sendTurn]);

  return {
    items,
    pendingConfirmation,
    isStreaming,
    sendUserMessage,
    confirmPending,
    stopStream,
  };
};
